import classNames from 'classnames';
import React, {useMemo} from 'react';

import {
    AcademyPresented,
    AcademySelected,
    GwgAccelerator,
    GwgCarousel,
} from '@udemy/career-academies';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {useActiveSubscriptions} from '@udemy/subscription-graphql';

import styles from './ai-learning-paths.module.less';

export interface AiLearningPathsSectionProps {
    /** Array of GWG accelerators to display as carousels */
    allGwgAccelerators: GwgAccelerator[];
}

export const AiLearningPathsSection = ({allGwgAccelerators}: AiLearningPathsSectionProps) => {
    const {gettext} = useI18n();

    const currentlySubscribedPlan = useActiveSubscriptions()?.[0];

    // Memoize subscription plans object to prevent unnecessary carousel re-renders
    const subscriptionPlans = useMemo(
        () => ({
            currentlySubscribedPlan,
        }),
        [currentlySubscribedPlan],
    );

    return (
        <div aria-labelledby="ai-learning-paths-section">
            <div className={styles.header}>
                <div className={styles['icon-casing']}>
                    <Icon glyph="certificate" size="large" label={false} />
                </div>
                <h2 id="ai-learning-paths-heading" className="ud-heading-xxl">
                    {gettext('Learning paths designed to help you thrive in an AI-driven world')}
                </h2>
                <div className={classNames('ud-text-lg', styles['description'])}>
                    {gettext(
                        "Whether you're looking to learn the fundamentals or enhance your professional skills, Google's flexible paths help you gain practical AI fluency at your own pace.",
                    )}
                </div>
            </div>

            <div className={styles.carousels}>
                {allGwgAccelerators.map((accelerator) => (
                    <TrackImpression
                        key={accelerator.id}
                        trackFunc={() => {
                            Tracker.publishEvent(
                                new AcademyPresented({
                                    academySlug: accelerator.slug,
                                    uiRegion: 'all_academies_careers_exploration',
                                }),
                            );
                        }}
                    >
                        <div>
                            <GwgCarousel
                                accelerator={accelerator}
                                currentlySubscribedPlan={subscriptionPlans.currentlySubscribedPlan}
                                onEnrollmentClick={() => {
                                    Tracker.publishEvent(
                                        new AcademySelected({
                                            academySlug: accelerator.slug,
                                            uiRegion: 'learn-ai-with-google',
                                        }),
                                    );
                                }}
                            />
                        </div>
                    </TrackImpression>
                ))}
            </div>
        </div>
    );
};
